import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../apiConfig'; // Assuming you have this
import { Container, Typography, Box, Grid, Modal, Paper, IconButton, Button, useMediaQuery, useTheme, Card, CardContent, CardMedia, CardActionArea } from '@mui/material';
import { styled, ThemeProvider, createTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CustomTypography from './UI/Typography/CustomTypography';
import AwarenessMaterialCard from './UI/Cards/AwarenessMaterialCard';
import CustomPager from './UI/Pagination/CustomPager';

const theme = createTheme({
  shape: {
    borderRadius: 5
  },
});

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#F9F9F9',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}));

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxWidth: '800px',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto'
};

const AwarenessMaterial = () => {
  const [allMaterials, setAllMaterials] = useState([]);
  const [displayedMaterials, setDisplayedMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const muiTheme = useTheme(); // For useMediaQuery
  const isSmallerScreen = useMediaQuery(muiTheme.breakpoints.down('sm')); // For responsive modal layout

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;

  // Security Advice flyers data
  const securityAdviceFlyers = [
    {
      id: 1,
      title: 'Insider Threat',
      description: 'Understanding the risks posed by insiders within an organisation.',
      coverImage: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/HECTOR-WP3-Flyer_1-FINAL.jpg`,
      pdfUrl: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/HECTOR-WP3-Flyer_1-FINAL.pdf`,
      languages: [
        { code: 'bg', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_Bulgaria.png`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/Bulgarian_flyer_1.pdf` },
        { code: 'el', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_Greece.png`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/Greek_flyer_1.pdf` },
        { code: 'fr', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_France.png`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/French_flyer_1.pdf` },
        { code: 'pl', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_Poland.webp`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/Polish_flyer_1.pdf` }
      ]
    },
    {
      id: 2,
      title: 'Good Housekeeping',
      description: 'Security guidelines for maintaining good housekeeping practices',
      coverImage: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/HECTOR-WP3-Flyer_2-FINAL.jpg`,
      pdfUrl: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/HECTOR-WP3-Flyer_2-FINAL.pdf`,
      languages: [
        { code: 'bg', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_Bulgaria.png`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/Bulgarian_flyer_2.pdf` },
        { code: 'el', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_Greece.png`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/Greek_flyer_2.pdf` },
        { code: 'fr', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_France.png`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/French_flyer_2.pdf` },
        { code: 'pl', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_Poland.webp`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/Polish_flyer_2.pdf` }
      ]
    },
    {
      id: 3,
      title: 'Rental Vehicles',
      description: 'Security measures for rental vehicle operations',
      coverImage: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/HECTOR-WP3-Flyer_3-FINAL.jpg`,
      pdfUrl: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/HECTOR-WP3-Flyer_3-FINAL.pdf`,
      languages: [
        { code: 'bg', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_Bulgaria.png`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/Bulgarian_flyer_3.pdf` },
        { code: 'el', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_Greece.png`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/Greek _flyer_3.pdf` },
        { code: 'fr', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_France.png`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/French_flyer_3.pdf` },
        { code: 'pl', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_Poland.webp`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/Polish_flyer_3.pdf` }
      ]
    },
    {
      id: 4,
      title: 'CBRN Threats',
      description: 'Guidelines for Chemical, Biological, Radiological, and Nuclear threats',
      coverImage: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/HECTOR-WP3-Flyer_4-FINAL.jpg`,
      pdfUrl: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/HECTOR-WP3-Flyer_4-FINAL.pdf`,
      languages: [
        { code: 'bg', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_Bulgaria.png`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/Bulgarian_flyer_4.pdf` },
        { code: 'el', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_Greece.png`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/Greek _flyer_4.pdf` },
        { code: 'fr', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_France.png`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/French_flyer_4.pdf` },
        { code: 'pl', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_Poland.webp`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/Polish_flyer_4.pdf` }
      ]
    },
    {
      id: 5,
      title: 'How to Respond in a Crisis',
      description: 'Emergency response procedures and crisis management guidelines',
      coverImage: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/HECTOR-WP3-Flyer_5-FINAL.jpg`,
      pdfUrl: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/HECTOR-WP3-Flyer_5-FINAL.pdf`,
      languages: [
        { code: 'bg', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_Bulgaria.png`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/Bulgarian_flyer_5.pdf` },
        { code: 'el', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_Greece.png`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/Greek _flyer_5.pdf` },
        { code: 'fr', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_France.png`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/French_flyer_5.pdf` },
        { code: 'pl', flag: `${process.env.PUBLIC_URL}/images/Countries/Flag_of_Poland.webp`, pdf: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/Polish_flyer_5.pdf` }
      ]
    },
    {
      id: 6,
      title: 'Trifold Brochure',
      description: 'Comprehensive security awareness trifold brochure',
      coverImage: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/HECTOR-WP3-Trifold Brochure-1-FINAL.jpg`,
      pdfUrl: `${process.env.PUBLIC_URL}/AwarenessMaterial/Security Advice/HECTOR-WP3-Trifold Brochure-1-FINAL.pdf`,
      languages: []
    }
  ];

  // Dissemination Material data
  const disseminationMaterials = [
    {
      id: 1,
      title: 'HECTOR Project Booklet',
      description: 'Comprehensive guide to the HECTOR project objectives and outcomes',
      coverImage: `${process.env.PUBLIC_URL}/AwarenessMaterial/Dissemination material/1. HECTOR-BOOKLET-FINAL -1.jpg`,
      pdfUrl: `${process.env.PUBLIC_URL}/AwarenessMaterial/Dissemination material/1. HECTOR-BOOKLET-FINAL .pdf`
    },
    {
      id: 2,
      title: 'Newsletter vol. 1 (1st – 6th M) • 2024',
      description: 'HECTOR Project Magazine\nPhase I (Anticipate): Security Planning',
      coverImage: `${process.env.PUBLIC_URL}/AwarenessMaterial/Dissemination material/Hector_Newsletter 1-1.jpg`,
      pdfUrl: `${process.env.PUBLIC_URL}/AwarenessMaterial/Dissemination material/Hector_Newsletter 1.pdf`
    },
    {
      id: 3,
      title: 'Newsletter vol. 2 (7th – 12th M) 2024',
      description: 'HECTOR Project Magazine\nPhase ii (Protect): Physical security and technological solutions',
      coverImage: `${process.env.PUBLIC_URL}/AwarenessMaterial/Dissemination material/Hector_Newsletter 2-1.jpg`,
      pdfUrl: `${process.env.PUBLIC_URL}/AwarenessMaterial/Dissemination material/Hector_Newsletter 2.pdf`
    }
  ];

  // Videos data
  const videos = [
    {
      id: 1,
      title: 'What is an Insider Threat?',
      description: 'Understanding insider threats and their impact on security',
      thumbnail: `${process.env.PUBLIC_URL}/AwarenessMaterial/videos/Insider threat.jpg`,
      videoUrl: 'https://youtu.be/yquQL0lBYSA?si=YWroqg3RaPUJMtMd'
    },
    {
      id: 2,
      title: 'If It Feels Wrong… It Might Be',
      description: 'Trusting your instincts in security situations',
      thumbnail: `${process.env.PUBLIC_URL}/AwarenessMaterial/videos/Suspicious Items.jpg`,
      videoUrl: 'https://youtu.be/4bEi5I7ddBc?si=cKfSuY-Pp9Tg6aGp'
    },
    {
      id: 3,
      title: 'A Simple Plan for When Seconds Matter',
      description: 'Emergency response planning for critical situations',
      thumbnail: `${process.env.PUBLIC_URL}/AwarenessMaterial/videos/RUN HIDE TELL.jpg`,
      videoUrl: 'https://youtu.be/FUjnyfsCIKo?si=qYoSLecSZZ5e507x'
    }
  ];

  useEffect(() => {
    axios.get(`${API_URL}/awareness-material/public`)
    .then(response => {
      setAllMaterials(response.data);
      setLoading(false);
    })
    .catch(error => {
      console.error("Error fetching awareness materials:", error);
      setAllMaterials([]);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (allMaterials.length > 0) {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      setDisplayedMaterials(allMaterials.slice(startIndex, endIndex));
    } else {
      setDisplayedMaterials([]);
    }
  }, [allMaterials, currentPage, itemsPerPage]);

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0);
  };

  const handleOpenModal = (material) => {
    if (material.fullArticleContent) {
      setSelectedMaterial(material);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedMaterial(null);
  };

  const handlePdfClick = (pdfUrl) => {
    window.open(pdfUrl, '_blank');
  };

  const handleVideoClick = (videoUrl) => {
    window.open(videoUrl, '_blank');
  };

  if (loading) {
    return <Container maxWidth="lg" style={{ marginTop: '100px' }}><Typography>Loading awareness materials...</Typography></Container>;
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xxl">
        <Box sx={{ flexGrow: 1, bgcolor: 'white' }}>

          <Grid container spacing={2} style={{ marginBottom: '25px', marginTop: '50px' }}>
            <Grid item xs={12}>
              <Box textAlign="center">
                <CustomTypography variant="h3" text="Awareness Material" color="#0651A0" underlineThickness={3} underline={true} />
              </Box>
            </Grid>
          </Grid>

          {/* Security Advice Section */}
          <Grid container spacing={2} style={{ marginBottom: '50px' }}>
            <Grid item xs={12}>
              <Box textAlign="center" style={{ marginBottom: '30px' }}>
                <CustomTypography variant="h4" text="Security Advice" color="#0651A0" underlineThickness={2} underline={true} />
              </Box>
            </Grid>
            <Grid container spacing={3} style={{ justifyContent: 'center' }}>
              {securityAdviceFlyers.map((flyer) => (
                <Grid item xs={12} sm={6} md={3} key={flyer.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardActionArea onClick={() => handlePdfClick(flyer.pdfUrl)} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                      <CardMedia
                        component="img"
                        height="240"
                        image={flyer.coverImage}
                        alt={flyer.title}
                        sx={{ objectFit: 'contain', background: '#f4f4f4', p: 1, mb: 1, borderRadius: 2 }}
                      />
                      <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 0 }}>
                        <Typography gutterBottom variant="h6" component="h2" sx={{ mt: 1 }}>
                          {flyer.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {flyer.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                    {flyer.languages && flyer.languages.length > 0 && (
                      <Box sx={{ p: 2, pt: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {flyer.languages.map((lang) => (
                          <IconButton
                            key={lang.code}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePdfClick(lang.pdf);
                            }}
                            sx={{
                              padding: '4px',
                              '&:hover': {
                                transform: 'scale(1.1)',
                                transition: 'transform 0.2s'
                              }
                            }}
                            aria-label={`Open ${flyer.title} in ${lang.code === 'bg' ? 'Bulgarian' : lang.code === 'el' ? 'Greek' : lang.code === 'fr' ? 'French' : 'Polish'}`}
                          >
                            <img
                              src={lang.flag}
                              alt={lang.code === 'bg' ? 'Bulgarian' : lang.code === 'el' ? 'Greek' : lang.code === 'fr' ? 'French' : 'Polish'}
                              style={{
                                width: '32px',
                                height: '24px',
                                objectFit: 'cover',
                                borderRadius: '2px',
                                cursor: 'pointer',
                                border: '1px solid #e0e0e0'
                              }}
                            />
                          </IconButton>
                        ))}
                      </Box>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Dissemination Material Section */}
          <Grid container spacing={2} style={{ marginBottom: '50px' }}>
            <Grid item xs={12}>
              <Box textAlign="center" style={{ marginBottom: '30px' }}>
                <CustomTypography variant="h4" text="Dissemination Material" color="#0651A0" underlineThickness={2} underline={true} />
              </Box>
            </Grid>
            <Grid container spacing={3} style={{ justifyContent: 'center' }}>
              {disseminationMaterials.map((material) => (
                <Grid item xs={12} sm={6} md={4} key={material.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardActionArea onClick={() => handlePdfClick(material.pdfUrl)} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                      <CardMedia
                        component="img"
                        height="250"
                        image={material.coverImage}
                        alt={material.title}
                        sx={{ objectFit: 'cover' }}
                      />
                      <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                        <Typography gutterBottom variant="h6" component="h2">
                          {material.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {material.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Videos Section */}
          <Grid container spacing={2} style={{ marginBottom: '50px' }}>
            <Grid item xs={12}>
              <Box textAlign="center" style={{ marginBottom: '30px' }}>
                <CustomTypography variant="h4" text="Videos" color="#0651A0" underlineThickness={2} underline={true} />
              </Box>
            </Grid>
            <Grid container spacing={3} style={{ justifyContent: 'center' }}>
              {videos.map((video) => (
                <Grid item xs={12} sm={6} md={4} key={video.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardActionArea onClick={() => handleVideoClick(video.videoUrl)} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                      <Box sx={{ position: 'relative' }}>
                        <CardMedia
                          component="img"
                          height="200"
                          image={video.thumbnail}
                          alt={video.title}
                          sx={{ objectFit: 'cover' }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            borderRadius: '50%',
                            width: 60,
                            height: 60,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <PlayArrowIcon sx={{ color: 'white', fontSize: 40 }} />
                        </Box>
                      </Box>
                      <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                        <Typography gutterBottom variant="h6" component="h2">
                          {video.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {video.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Original Awareness Materials Section (if needed) */}
          {allMaterials.length > 0 && (
            <>
              <Grid container spacing={2} style={{ marginBottom: '25px' }}>
                <Grid item xs={12}>
                  <Box textAlign="center">
                    <CustomTypography variant="h4" text="Additional Materials" color="#0651A0" underlineThickness={2} underline={true} />
                  </Box>
                </Grid>
              </Grid>

              <Grid container spacing={3} style={{ paddingTop: '10px', paddingBottom: '50px', justifyContent: 'center', alignItems: 'flex-start' }}>
                {displayedMaterials.map((material) => (
                  <Grid item xs={12} sm={12} md={10} lg={10} key={material.id} style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                    <AwarenessMaterialCard
                      id={material.id}
                      title={material.title}
                      description={material.shortDescription}
                      iconUrl={material.iconFileUrl}
                      pdfUrl={material.pdfFileUrl}
                      onTitleClick={material.fullArticleContent ? () => handleOpenModal(material) : undefined}
                      style={{ width: '100%' }}
                    />
                  </Grid>
                ))}
              </Grid>

              {allMaterials.length > itemsPerPage && (
                <Grid container spacing={2} style={{ justifyContent: 'center', alignItems: 'center', paddingBottom: '50px' }}>
                  <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center'}}>
                    <CustomPager
                      pageCount={Math.ceil(allMaterials.length / itemsPerPage)}
                      currentPage={currentPage}
                      onPageChange={handlePageChange}
                    />
                  </Grid>
                </Grid>
              )}
            </>
          )}

          {/* Modal for full article content */}
          {selectedMaterial && (
            <Modal
              open={modalOpen}
              onClose={handleCloseModal}
              aria-labelledby="awareness-material-title"
              aria-describedby="awareness-material-description"
            >
              <Paper sx={modalStyle}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1 }}>
                  <IconButton onClick={handleCloseModal} aria-label="close">
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Grid container spacing={3}>
                  {selectedMaterial.iconFileUrl && (
                    <Grid item xs={12} sm={4} md={3} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                      <img 
                        src={selectedMaterial.iconFileUrl} 
                        alt={`${selectedMaterial.title} icon`} 
                        style={{ width: '100%', height: 'auto', maxHeight: '250px', objectFit: 'contain', borderRadius: '4px' }} 
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={selectedMaterial.iconFileUrl ? 8 : 12} md={selectedMaterial.iconFileUrl ? 9 : 12}>
                    <Typography id="awareness-material-title" variant="h4" component="h2" gutterBottom sx={{ textTransform: 'uppercase'}}>
                      {selectedMaterial.title}
                    </Typography>
                    <Typography
                      id="awareness-material-description"
                      component="div"
                      sx={{ whiteSpace: 'pre-wrap', mt: 2, maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}
                      dangerouslySetInnerHTML={{ __html: selectedMaterial.fullArticleContent }}
                    />
                    {selectedMaterial.pdfFileUrl && (
                      <Button 
                          variant="contained" 
                          href={selectedMaterial.pdfFileUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          startIcon={<DownloadIcon />}
                          sx={{mt:3}}
                      >
                          Download PDF
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Modal>
          )}

          {/* Footer Section */}
          <Grid container spacing={2} style={{ paddingTop: '50px' }}>
            <Grid item xs={12} sm={12} md={12}>
              <Item style={{ paddingTop: '50px', paddingBottom: '50px', backgroundImage: `url(${process.env.PUBLIC_URL}/images/BannerImages/footer.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" padding="20px">
                  <Box display="flex" alignItems="center">
                    <img src={`${process.env.PUBLIC_URL}/images/eu-flag.png`} alt="European Union Flag" style={{ width: '100px', height: 'auto', marginRight: '10px' }} />
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-start" flex="1">
                    <CustomTypography variant="h7" text="This project is funded by the European Union's Internal Security Fund — Police." color="white" />
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="center" flex="1">
                    <CustomTypography variant="h7" text="Grant Agreement No. 101100558 — PROJECT HECTOR" color="white" />
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-end" flex="1">
                    <CustomTypography variant="h7" text="The content of this webpage represents the views of the author only and is his/her sole responsibility. The European Commission does not accept any responsibility for use that may be made of the information it contains." color="white" />
                  </Box>
                </Box>
              </Item>
            </Grid>
          </Grid>

        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default AwarenessMaterial;